import { cn } from "@repo/shared/utils";
import { mapObject } from "@repo/lib/mapObjects";
import { SelectItem } from "@repo/ui/components/Base/Select";
import { SelectInput } from "@repo/ui/components/Base/SelectInput";
import { useFieldMapping } from "./useFieldMapping";
import { LaserficheFieldMapping } from "./LaserficheFieldMapping";
import {
  FieldMappingContext,
  type FieldMappingContextType,
} from "./FieldMappingContext";
import type { LaserficheFieldSourceDetails } from "./laserficheFieldSources";
import type {
  LaserficheInspectField,
  LaserficheInspectTemplate,
  LaserfichePutField,
  LaserfichePutTemplate,
} from "@repo/types";
import { useMemo } from "react";

export interface LaserficheTemplatePickerProps {
  disabled?: boolean;
  availableTemplates: LaserficheInspectTemplate[];
  availableFields: LaserficheInspectField[];
  availableSources: (LaserficheFieldSourceDetails & { key: string })[];
  value?: LaserfichePutTemplate | null;
  onChange?: (newValue: LaserfichePutTemplate | null) => void;
  className?: string;
}

export const LaserficheTemplatePicker = ({
  disabled = false,
  availableTemplates,
  availableFields,
  availableSources,
  value,
  onChange,
  className,
}: LaserficheTemplatePickerProps) => {
  const fieldMap = useFieldMapping(availableFields);

  const template = availableTemplates.find(
    (template) => template.id === value?.templateId
  );

  if (value?.templateFieldAssignments) {
    value.templateFieldAssignments = value.templateFieldAssignments.filter(
      (field) => field.fieldId && (field.sourceKey || field.constantValue)
    );
  }

  const valueFields = useMemo(
    () =>
      mapObject(
        value?.templateFieldAssignments ?? [],
        (field) => "" + field.fieldId
      ),
    [value]
  ) as Record<number, LaserfichePutField>;

  const context: FieldMappingContextType = {
    disabled,
    mutableId: false,
    availableSources,
    availableFields,
    fieldMap,
  };

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <SelectInput
        disabled={disabled}
        onValueChange={(newValue) => {
          if (newValue === "none") {
            onChange?.(null);
          } else {
            onChange?.({
              templateFieldAssignments: [],
              ...value,
              templateId: +newValue,
            });
          }
        }}
        value={"" + (value?.templateId ?? "none")}
        options={[
          <SelectItem key="none" value="none">
            No Template
          </SelectItem>,
          ...availableTemplates.map((template) => (
            <SelectItem key={template.id} value={"" + template.id}>
              {template.name}
            </SelectItem>
          )),
        ]}
      />

      <div className="flex flex-col gap-3">
        <FieldMappingContext.Provider value={context}>
          {template ? (
            template.fields.map((id) => (
              <LaserficheFieldMapping
                key={id}
                value={valueFields[id] ?? { fieldId: id, sourceKey: "" }}
                onChange={(_prevValue, newValueField) => {
                  const templateFieldAssignments =
                    value?.templateFieldAssignments ?? [];
                  let affected = templateFieldAssignments.find(
                    (field) => field.fieldId === newValueField.fieldId
                  );

                  if (!affected) {
                    affected = { ...newValueField };
                    templateFieldAssignments.push(affected);
                  }

                  affected.sourceKey = newValueField.sourceKey;
                  affected.constantValue = newValueField.constantValue;

                  onChange?.({
                    templateId: 0,
                    ...value,
                    templateFieldAssignments,
                  });
                }}
              />
            ))
          ) : (
            <p className="opacity-50">No Template Selected</p>
          )}
        </FieldMappingContext.Provider>
      </div>
    </div>
  );
};
